import { useCallback, useEffect, useState } from 'react';
import { Card } from 'react-bootstrap';
import { useHistory } from 'react-router-dom';
import { Loader, Typography, TablePagination } from '@launchmetrics/lm-ui';
import K2Api from 'lib/ajax/K2Api';
import Breadcrumb from 'components/LM/common/Breadcrumb';
import './validationManageLists.scss';

const k2 = new K2Api();
const DEFAULT_PAGE_SIZE = 20;

function formatDate(dateStr) {
	if (!dateStr) return '—';
	return dateStr.slice(0, 10);
}

function buildFilterBadges(filters) {
	if (!filters || typeof filters !== 'object') return [];
	const badges = [];

	const arrayFilters = [
		['type', 'type'],
		['status', 'status'],
		['country', 'country'],
		['channels', 'channels'],
		['labels', 'labels'],
		['social', 'social'],
		['language', 'language'],
		['gender', 'gender'],
		['tag', 'tag'],
		['voice_tag', 'voice_tag'],
		['follower_channels', 'follower_channels'],
		['last_edited_by', 'edited by'],
		['upload_csv_file', 'csv'],
	];

	for (const [key, label] of arrayFilters) {
		const val = filters[key];
		if (Array.isArray(val) && val.length) {
			badges.push(`${label}: ${val.join(', ')}`);
		}
	}

	const stringFilters = ['voice_name', 'name', 'email'];
	for (const key of stringFilters) {
		if (filters[key]) {
			badges.push(`${key}: ${filters[key]}`);
		}
	}

	if (filters.to_merge_only) badges.push('to merge: yes');
	if (filters.voice_name_duplicates) badges.push('duplicates: yes');
	if (filters.missing_mandatory_fields) badges.push('missing fields: yes');
	if (filters.has_recent_publications != null) badges.push('no data (last 12 months)');
	if (filters.has_recent_brand_mentions != null) badges.push('no branded data (last 12 months)');
	if (filters.profile_with_issues === true) badges.push('profile issues: yes');
	if (filters.profile_with_issues === false) badges.push('profile issues: no');

	if (filters.followers_min != null || filters.followers_max != null) {
		const parts = [];
		if (filters.followers_min != null) parts.push(`min: ${filters.followers_min}`);
		if (filters.followers_max != null) parts.push(`max: ${filters.followers_max}`);
		badges.push(`followers ${parts.join(', ')}`);
	}
	if (filters.followers && (filters.followers.min != null || filters.followers.max != null)) {
		const parts = [];
		if (filters.followers.min != null) parts.push(`min: ${filters.followers.min}`);
		if (filters.followers.max != null) parts.push(`max: ${filters.followers.max}`);
		badges.push(`followers ${parts.join(', ')}`);
	}

	if (filters.type_confidence_min != null || filters.type_confidence_max != null) {
		badges.push(`type confidence: ${filters.type_confidence_min ?? 0}–${filters.type_confidence_max ?? 100}`);
	}
	if (filters.country_confidence_min != null || filters.country_confidence_max != null) {
		badges.push(`country confidence: ${filters.country_confidence_min ?? 0}–${filters.country_confidence_max ?? 100}`);
	}
	if (filters.confidence && (filters.confidence.min != null || filters.confidence.max != null)) {
		badges.push(`confidence: ${filters.confidence.min ?? 0}–${filters.confidence.max ?? 100}`);
	}

	if (Array.isArray(filters.voice_ids) && filters.voice_ids.length) {
		badges.push(filters.combine_with_filters ? 'VOICE IDS + FILTERS' : 'VOICE IDS');
	}

	if (filters.last_edit_date_start || filters.last_edit_date_end) {
		const parts = [];
		if (filters.last_edit_date_start) parts.push(`from: ${filters.last_edit_date_start}`);
		if (filters.last_edit_date_end) parts.push(`to: ${filters.last_edit_date_end}`);
		badges.push(`edit date ${parts.join(', ')}`);
	}

	return badges;
}

function buildRunUrl(filters, listId) {
	const params = new URLSearchParams();
	if (listId) params.set('list_id', listId);
	if (!filters || typeof filters !== 'object') {
		const qs = params.toString();
		return qs ? `/voices/validation/search?${qs}` : '/voices/validation/search';
	}

	if (filters.voice_name) params.set('term', filters.voice_name);
	if (filters.name) params.set('term', filters.name);
	if (Array.isArray(filters.type) && filters.type.length) params.set('type', filters.type.join(','));
	if (Array.isArray(filters.channels) && filters.channels.length) params.set('channel', filters.channels.join(','));
	if (Array.isArray(filters.channel) && filters.channel.length) params.set('channel', filters.channel.join(','));
	if (Array.isArray(filters.country) && filters.country.length) params.set('country', filters.country.join(','));
	if (Array.isArray(filters.labels) && filters.labels.length) params.set('labels', filters.labels.join(','));
	if (Array.isArray(filters.status) && filters.status.length) params.set('status', filters.status.join(','));
	if (typeof filters.validated === 'boolean') params.set('validated', String(filters.validated));
	if (Array.isArray(filters.upload_csv_file) && filters.upload_csv_file.length) params.set('upload_csv_file_id', filters.upload_csv_file.join(','));
	if (Array.isArray(filters.last_edited_by) && filters.last_edited_by.length) params.set('last_edited_by', filters.last_edited_by.join(','));

	if (filters.type_confidence_min != null) params.set('type_confidence_min', filters.type_confidence_min);
	if (filters.type_confidence_max != null) params.set('type_confidence_max', filters.type_confidence_max);
	if (filters.country_confidence_min != null) params.set('country_confidence_min', filters.country_confidence_min);
	if (filters.country_confidence_max != null) params.set('country_confidence_max', filters.country_confidence_max);

	if (filters.to_merge_only) params.set('to_merge_only', 'true');
	if (filters.missing_mandatory_fields) params.set('missing_mandatory_fields', 'true');
	if (filters.has_recent_publications != null) params.set('has_recent_publications', 'false');
	if (filters.has_recent_brand_mentions != null) params.set('has_recent_brand_mentions', 'false');
	if (filters.voice_name_duplicates) params.set('voice_name_duplicates', 'true');

	if (Array.isArray(filters.voice_ids) && filters.voice_ids.length) params.set('voice_ids', filters.voice_ids.join(','));
	if (filters.combine_with_filters) params.set('combine_with_filters', 'true');
	if (Array.isArray(filters.follower_channels) && filters.follower_channels.length) params.set('follower_channels', filters.follower_channels.join(','));
	if (filters.followers_min != null) params.set('followers_min', filters.followers_min);
	if (filters.followers_max != null) params.set('followers_max', filters.followers_max);

	if (filters.last_edit_date_start) {
		const d = filters.last_edit_date_start;
		params.set('last_edited_date_start', d.includes('T') ? d : d + 'T00:00:00+00:00');
	}
	if (filters.last_edit_date_end) {
		const d = filters.last_edit_date_end;
		params.set('last_edited_date_end', d.includes('T') ? d : d + 'T23:59:59+00:00');
	}

	const qs = params.toString();
	return qs ? `/voices/validation/search?${qs}` : '/voices/validation/search';
}

function ValidationManageLists() {
	const history = useHistory();
	const [loading, setLoading] = useState(true);
	const [lists, setLists] = useState([]);
	const [total, setTotal] = useState(0);
	const [error, setError] = useState(null);
	const [deleting, setDeleting] = useState(null);
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

	const fetchLists = useCallback(async () => {
		setLoading(true);
		setError(null);
		try {
			const from = (page - 1) * pageSize;
			const res = await k2.get('/voices/validation_filter_lists', { params: { size: pageSize, from } });
			setLists(res.data?.lists || []);
			setTotal(res.data?.total ?? 0);
		} catch (err) {
			setError(err);
		} finally {
			setLoading(false);
		}
	}, [page, pageSize]);

	useEffect(() => {
		fetchLists();
	}, [fetchLists]);

	const handleDelete = async (id, name) => {
		if (!window.confirm(`Delete list "${name}"?`)) return;
		setDeleting(id);
		try {
			await k2.delete(`/voices/validation_filter_lists/${id}`);
			fetchLists();
		} catch {
			alert('Failed to delete list');
		} finally {
			setDeleting(null);
		}
	};

	const handleRun = (filters, listId) => {
		history.push(buildRunUrl(filters, listId));
	};

	return (
		<div className="validation-manage-lists">
			<Breadcrumb items={['Validation', 'Manage Lists']} />
			<Card>
				<Card.Body>
					<div className="manage-lists-header">
						<Typography variant="h6">Saved Validation Lists</Typography>
						<Typography variant="body2" className="lists-count">{total} lists</Typography>
					</div>

					{loading && <Loader />}

					{error && (
						<div className="manage-lists-error">
							<Typography variant="body2" color="error">Failed to load lists.</Typography>
						</div>
					)}

					{!loading && !error && (
						<div className="manage-lists-table-wrapper">
							<table className="manage-lists-table">
								<thead>
									<tr>
										<th>ID</th>
										<th>Name</th>
										<th>Filters / Source</th>
										<th>Created_by</th>
										<th>Created</th>
										<th>Updated</th>
										<th></th>
									</tr>
								</thead>
								<tbody>
									{lists.map(item => {
										const badges = buildFilterBadges(item.filters);
										return (
											<tr key={item.id}>
												<td className="col-id">{item.id}</td>
												<td className="col-name">{item.name}</td>
												<td className="col-filters">
													{badges.map((badge, i) => (
														<span key={i} className="filter-badge">{badge}</span>
													))}
													{badges.length === 0 && <span className="no-filters">—</span>}
												</td>
												<td className="col-created-by">{item.created_by || '—'}</td>
												<td className="col-date">{formatDate(item.inserted_at)}</td>
												<td className="col-date">{formatDate(item.updated_at)}</td>
												<td className="col-actions">
													<button
														className="action-btn action-btn--run"
														onClick={() => handleRun(item.filters, item.id)}
													>
														&#9654; Run
													</button>
													<button
														className="action-btn"
														onClick={() => history.push(`/voices/validation/search?list_id=${item.id}`)}
													>
														Edit
													</button>
													<button
														className="action-btn action-btn--delete"
														disabled={deleting === item.id}
														onClick={() => handleDelete(item.id, item.name)}
													>
														{deleting === item.id ? '...' : 'Delete'}
													</button>
												</td>
											</tr>
										);
									})}
									{lists.length === 0 && (
										<tr>
											<td colSpan={7} className="no-data">No saved lists found.</td>
										</tr>
									)}
								</tbody>
							</table>
							<div className="d-flex flex-row justify-content-end align-items-center mt-3 gap-3">
								{loading && <Loader size={40} />}
								<TablePagination
									onRowsPerPageChange={newSize => { setPageSize(newSize); setPage(1); }}
									onPageChange={nextPage => setPage(nextPage)}
									count={total}
									rowsPerPage={pageSize}
									page={page}
								/>
							</div>
						</div>
					)}
				</Card.Body>
			</Card>
		</div>
	);
}

export default ValidationManageLists;
